import { useAchievementTypes, useDateNow, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { CalendarEventDateString } from 'components/calendar';
import {
  ApiDownloadButton,
  DisplayAttachments,
  DisplayFormattedDate,
  FlexContainer,
  ModalButton,
  ProductLinkWithIcon,
  TextWithLineBreaks,
  TimeIndicator,
  User
} from 'components/common';
import {
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { AttributeDisplayValue } from 'components/common/customAttributes';
import { AchievementCompletionItemsRender } from 'components/achievements';

import type { GetCompletionsRes } from 'shared-types/responses';
import type { ButtonInterface } from 'components/common/Button';

interface Props {
  completions: GetCompletionsRes['completions'];
}

const CompletionHistoryTable = ({ completions }: Props) => {
  const {
    language,
    messages,
    serverConfig: {
      analyticsHideProductStartDate,
      productCustomAttributes,
      workloadInDays,
      workloadInDaysHoursPerDay
    },
    theme,
    user
  } = useReduxState();

  const { dateNow } = useDateNow();

  const achievementTypes = useAchievementTypes();

  const types = {
    'calendar-event': messages.eventCalendarEvent,
    course: messages.commonCourse,
    'individual-achievement': messages.individualAchievement,
    path: messages.commonPath
  };

  const infoButtonProps = {
    category: 'lighter',
    iconStyle: { fontSize: '1.1rem' },
    size: 'small',
    style: {
      borderRadius: 20,
      fontSize: '1.2rem',
      fontWeight: 600,
      padding: '3px 9px 4px 7px'
    }
  } as ButtonInterface;

  const filteredCustomAttributes = productCustomAttributes?.filter(
    (attr) => attr.displayInAnalytics
  );

  return (
    <BoxOverflowWrapper withStickyHeader={true}>
      <BoxTable>
        <BoxTableHeader sticky={true}>
          <BoxTableCell>{messages.commonTitle}</BoxTableCell>
          <BoxTableCell>{messages.commonUser}</BoxTableCell>
          <BoxTableCell>{messages.commonType}</BoxTableCell>
          <BoxTableCell>{messages.commonCompleted}</BoxTableCell>
          {!analyticsHideProductStartDate ? (
            <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
              {messages.commonStartDate}
            </BoxTableCell>
          ) : null}
          <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
            {messages.completionsCompletionDate}
          </BoxTableCell>
          <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
            {messages.commonExpirationDate}
          </BoxTableCell>
          <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
            {`${messages.excelWorkload} (${messages.completionWorkloadHours})`}
          </BoxTableCell>

          {workloadInDays ? (
            <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
              {`${
                messages.excelWorkload
              } (${messages.commonDays.toLowerCase()})`}
            </BoxTableCell>
          ) : null}

          {filteredCustomAttributes?.map((attribute) => (
            <BoxTableCell key={attribute.key} style={{ whiteSpace: 'nowrap' }}>
              {attribute.titleData?.[language] || attribute.title}
            </BoxTableCell>
          ))}

          <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
            {messages.commonCustomFields}
          </BoxTableCell>

          <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
            {messages.commonOtherInformation}
          </BoxTableCell>
        </BoxTableHeader>

        {completions.map((completion, i) => {
          const customFields =
            completion.type === 'achievement'
              ? completion.achievement.customFields?.filter(
                  (attribute) =>
                    completion.userAttributes?.[attribute.id] !== undefined
                )
              : null;

          return (
            <BoxTableRow key={i}>
              <BoxTableCell>
                <div className="info">
                  {(completion.type === 'course' && !completion.course) ||
                  (completion.type === 'path' && !completion.path) ? (
                    <div className="title">{completion.title}</div>
                  ) : (
                    <ProductLinkWithIcon
                      {...(completion.type === 'achievement'
                        ? {
                            achievement: completion.achievement,
                            linkHref: `/users/${completion.user.id}/achievements/${completion.achievement.id}`
                          }
                        : completion.type === 'calendar-event'
                        ? {
                            calendarEvent: completion.calendarEvent
                          }
                        : completion.type === 'course'
                        ? {
                            course: completion.course,
                            linkHref: `/users/${completion.user.id}/courses/${completion.course.id}`
                          }
                        : completion.type === 'path'
                        ? {
                            path: completion.path,
                            linkHref: `/users/${completion.user.id}/paths/${completion.path.id}`
                          }
                        : {
                            // individual achievement
                            individualAchievement: completion,
                            linkHref: `/achievements/individual?titleSearch=${completion.title}`
                          })}
                    >
                      {completion.type === 'calendar-event' && (
                        <CalendarEventDateString
                          startDate={
                            completion.calendarEvent.startDate as string
                          }
                          endDate={completion.calendarEvent.endDate as string}
                        />
                      )}
                    </ProductLinkWithIcon>
                  )}
                </div>
              </BoxTableCell>

              <BoxTableCell>
                <User
                  contentStyle={{ maxWidth: 300 }}
                  enableProfileLink={true}
                  style={{ marginRight: 30, minWidth: 200 }}
                  user={completion.user}
                />
              </BoxTableCell>

              <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                {completion.type === 'achievement'
                  ? achievementTypes[completion.achievement.type].title
                  : types[completion.type]}
              </BoxTableCell>

              <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                <TimeIndicator
                  language={language}
                  timeString={completion.completionDate as string}
                />
              </BoxTableCell>

              {!analyticsHideProductStartDate ? (
                <BoxTableCell>
                  {completion.startedAtDate ? (
                    <span className="date-started">
                      <DisplayFormattedDate date={completion.startedAtDate} />
                    </span>
                  ) : null}
                </BoxTableCell>
              ) : null}

              <BoxTableCell>
                <span
                  className="date-completed"
                  style={
                    completion.completionDate &&
                    Date.parse(completion.completionDate as string) > dateNow
                      ? { color: theme.warningStandaloneColor }
                      : {}
                  }
                >
                  <DisplayFormattedDate date={completion.completionDate} />
                </span>
              </BoxTableCell>

              <BoxTableCell>
                {completion.expirationDate ? (
                  <span
                    className={`${
                      Date.parse(completion.expirationDate as string) < dateNow
                        ? 'date-expired'
                        : ''
                    }`}
                  >
                    <DisplayFormattedDate date={completion.expirationDate} />
                  </span>
                ) : null}
              </BoxTableCell>

              <BoxTableCell>
                {completion.workload
                  ? new Intl.NumberFormat(language).format(completion.workload)
                  : null}
              </BoxTableCell>

              {workloadInDays ? (
                <BoxTableCell>
                  {completion.workload
                    ? new Intl.NumberFormat(language).format(
                        Number(
                          (
                            completion.workload / workloadInDaysHoursPerDay
                          ).toFixed(2)
                        )
                      )
                    : null}
                </BoxTableCell>
              ) : null}

              {filteredCustomAttributes?.map((attribute) => (
                <BoxTableCell
                  key={attribute.key}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <AttributeDisplayValue
                    value={
                      completion.type === 'achievement'
                        ? completion.achievement?.customAttributes?.[
                            attribute.key
                          ]
                        : completion.type === 'calendar-event'
                        ? completion.calendarEvent?.customAttributes?.[
                            attribute.key
                          ]
                        : completion.type === 'course'
                        ? completion.course?.customAttributes?.[attribute.key]
                        : completion.type === 'path'
                        ? completion.path?.customAttributes?.[attribute.key]
                        : undefined
                    }
                  />
                </BoxTableCell>
              ))}

              <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                {completion.type === 'achievement' && customFields?.length ? (
                  <FlexContainer style={{ gap: 20 }}>
                    {customFields.map((attribute, attributeIndex) => (
                      <div key={attributeIndex}>
                        <span style={{ fontWeight: 400 }}>
                          {attribute.titleTranslations?.[language] ||
                            attribute.title}
                          {': '}
                        </span>

                        <AttributeDisplayValue
                          style={{ display: 'inline-block' }}
                          value={completion.userAttributes[attribute.id]}
                        />
                      </div>
                    ))}
                  </FlexContainer>
                ) : null}
              </BoxTableCell>

              <BoxTableCell>
                <div className="info-details">
                  {(completion.type === 'achievement' ||
                    completion.type === 'individual-achievement') &&
                  completion.comments ? (
                    <ModalButton
                      icon="comment"
                      modalProps={{
                        closeButton: true,
                        title: messages.commonComment,
                        width: 'medium'
                      }}
                      text={messages.commonComment}
                      {...infoButtonProps}
                    >
                      <div className="comment">
                        <TextWithLineBreaks text={completion.comments} />
                      </div>
                    </ModalButton>
                  ) : null}

                  {completion.type === 'individual-achievement' ||
                  (completion.type === 'achievement' &&
                    completion.achievement?.certificateAllowDownloading) ? (
                    <DisplayAttachments
                      attachments={completion.attachments}
                      isConfidential={
                        completion.type === 'achievement'
                          ? !completion.canSeeAttachments
                          : false
                      }
                      modalButtonProps={infoButtonProps}
                    />
                  ) : null}

                  {completion.type === 'achievement' ? (
                    <AchievementCompletionItemsRender
                      authorized={completion.canSeeCompletionItems}
                      cleared={
                        completion.completionItemsClearedDate as unknown as string
                      }
                      confidential={completion.isConfidential}
                      items={completion.template}
                      renderInModal={true}
                      renderInModalProps={infoButtonProps}
                      targetUserId={user?.id}
                      type={completion.achievement?.type}
                    />
                  ) : null}

                  {completion.type === 'course' &&
                  completion.course?.certificateAllowDownloading ? (
                    <ApiDownloadButton
                      filename={`${completion.title}.pdf`}
                      icon={['far', 'file-pdf']}
                      text={messages.commonCertificate}
                      url={
                        `/admin/certificate` +
                        `?completionId=${completion.id}` +
                        `&language=${language}`
                      }
                      {...infoButtonProps}
                    />
                  ) : null}
                </div>
              </BoxTableCell>
            </BoxTableRow>
          );
        })}
      </BoxTable>

      <style jsx>{`
        .info {
          padding: 5px 0;
        }
        .title {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 700;
        }
        .info-details {
          display: flex;
          gap: 10px;
        }
        .date-completed {
          color: ${theme.successStandaloneColor};
        }
        .date-expired {
          color: ${theme.dangerColor};
        }
        .comment {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.cardBorderRadius};
          color: ${theme.textColor};
          font-size: 1.5rem;
          font-weight: 400;
          padding: 10px;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .info {
            min-width: 420px;
          }
        }
      `}</style>
    </BoxOverflowWrapper>
  );
};

export default CompletionHistoryTable;
